import { useTranslation } from 'react-i18next';
import { Mail, Send, Unlink } from 'lucide-react';
import { ApiError } from '../lib/api';
import { errorText } from '../i18n';
import { Menu, toast } from '@vatman/ui';
import { EMAIL_GROUPS, EMAIL_MODES, usePrefs, useSetEmailMode, useSetPref, useTelegram, useTelegramActions } from './notifications';
import './notifications.css';

/** User-menu submenu: which notifications are also sent by email. Toggles keep the menu open. */
export function EmailPrefsMenu() {
  const { t } = useTranslation();
  const prefs = usePrefs(true);
  const setPref = useSetPref();
  const setMode = useSetEmailMode();
  const mode = prefs.data?.emailMode ?? 'instant';
  return (
    <Menu.Sub>
      <Menu.SubTrigger icon={<Mail size={16} />}>{t('notifications.emails')}</Menu.SubTrigger>
      <Menu.SubContent>
        <div className="notif-pref-hint">{t('notifications.modeHint')}</div>
        {EMAIL_MODES.map((m) => (
          <Menu.CheckboxItem
            key={m}
            checked={mode === m}
            disabled={!prefs.data}
            onCheckedChange={() => setMode(m).catch((err) => toast(errorText(err instanceof ApiError ? err.code : 'internal')))}
          >
            {t(`notifications.mode.${m}`)}
          </Menu.CheckboxItem>
        ))}
        <Menu.Separator />
        <div className="notif-pref-hint">{t('notifications.emailHint')}</div>
        {EMAIL_GROUPS.map((g) => {
          const on = prefs.data?.email[g] ?? true;
          return (
            <Menu.CheckboxItem
              key={g}
              checked={on}
              disabled={!prefs.data}
              onCheckedChange={(v) => {
                setPref(g, v).catch((err) => toast(errorText(err instanceof ApiError ? err.code : 'internal')));
              }}
            >
              {t(`notifications.email.${g}`)}
            </Menu.CheckboxItem>
          );
        })}
      </Menu.SubContent>
    </Menu.Sub>
  );
}

/**
 * User-menu submenu for Telegram: connect the chat with the bot, pick what goes there, disconnect.
 * Hidden when the server has no bot.
 */
export function TelegramPrefsMenu() {
  const { t } = useTranslation();
  const tg = useTelegram(true);
  const actions = useTelegramActions();
  const fail = (err: unknown) => toast(errorText(err instanceof ApiError ? err.code : 'internal'));
  if (!tg.data?.available) return null;
  const connect = () => {
    // Opened right away, inside the click: a window opened after the request would be blocked.
    const tab = window.open('', '_blank');
    actions
      .link()
      .then(({ url }) => {
        if (tab) {
          tab.opener = null;
          tab.location.href = url;
        } else window.location.href = url;
        toast(t('notifications.telegram.opened'));
      })
      .catch((err) => {
        tab?.close();
        fail(err);
      });
  };
  return (
    <Menu.Sub>
      <Menu.SubTrigger icon={<Send size={16} />}>{t('notifications.telegram.title')}</Menu.SubTrigger>
      <Menu.SubContent>
        {tg.data.linked ? (
          <>
            <div className="notif-pref-hint">{tg.data.username ? t('notifications.telegram.linked', { name: tg.data.username }) : t('notifications.telegram.linkedNoName')}</div>
            <Menu.Separator />
            <div className="notif-pref-hint">{t('notifications.telegram.groupsHint')}</div>
            {EMAIL_GROUPS.map((g) => (
              <Menu.CheckboxItem key={g} checked={tg.data.groups?.[g] ?? true} onCheckedChange={(v) => actions.setGroup(g, v).catch(fail)}>
                {t(`notifications.email.${g}`)}
              </Menu.CheckboxItem>
            ))}
            <Menu.Separator />
            <Menu.Item icon={<Unlink size={16} />} danger onSelect={() => actions.unlink().then(() => toast(t('notifications.telegram.disconnected')), fail)}>
              {t('notifications.telegram.disconnect')}
            </Menu.Item>
          </>
        ) : (
          <>
            <div className="notif-pref-hint">{tg.data.bot ? t('notifications.telegram.hint', { bot: tg.data.bot }) : t('notifications.telegram.hintNoBot')}</div>
            <Menu.Item icon={<Send size={16} />} onSelect={connect}>
              {t('notifications.telegram.connect')}
            </Menu.Item>
          </>
        )}
      </Menu.SubContent>
    </Menu.Sub>
  );
}
